import { Element, useEditor, useNode } from "@craftjs/core";
import { Popover } from "@headlessui/react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import type { Schedule, ScheduleItem } from "src/types";

import AddToCalendar from "@/components/add-to-calendar/add-to-calendar-component";
import TopLevelFloatingOptions from "@/components/editor/block-outline/floating-options/top-level-floating-component";
import Text from "@/components/editor/primitives/text/text-component";
import {
  BlockBackgroundColorWrapper,
  BlockMaxWidthWrapper,
} from "@/components/editor/shared/block-wrapper";
import { AvatarGroup } from "@/components/editor/shared/schedules/avatar-group";
import EmptyItem from "@/components/editor/shared/schedules/empty-item-component";
import Filter from "@/components/editor/shared/schedules/filter-component";
import { Notification } from "@/components/editor/shared/schedules/notification-component";
import PartsTagsDisplay from "@/components/editor/shared/schedules/parts-tags-display-component";
import ScheduleSettings from "@/components/editor/shared/schedules/schedule-settings/schedule-settings-component";
import { mapFirstLastNames } from "@/components/editor/shared/schedules/utils";
import { SearchInput } from "@/components/editor/themes/shared/search-input";
import { Calendar } from "@/components/icons/calendar";
import { useScheduleFilter } from "@/hooks/use-schedule-filter";
import { useScheduleTimezone } from "@/hooks/use-schedule-timezone";
import { useSchedulesWithEventTimezone } from "@/hooks/use-schedules-with-event-timezone";
import useViewportContext from "@/hooks/use-viewport-context";
import getFormattedScheduleTime from "@/utils/dates/getFormattedScheduleTime";

import {
  AddToCalendarButton,
  AddToCalendarWrapper,
  AvatarGroupWrapper,
  FilterWrapper,
  MobileAddToCalendarWrapper,
  ScheduleItemDate,
  ScheduleItemDescription,
  ScheduleItemSegment,
  ScheduleItemTitle,
  ScheduleItemTopBar,
  ScheduleItemWrapper,
  StyledMenuContent,
  TimelineContent,
  TimelineWrapper,
  Title,
  Wrapper,
} from "./default-schedule-component-styles";
import {
  DayNumber,
  DayWrapper,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "./tabs";

export default function DefaultSchedule(props: { schedules: Schedule[] }) {
  const {
    connectors: { connect },
    id,
  } = useNode();

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const { t } = useTranslation();
  const { viewport } = useViewportContext();
  const isMobile = React.useMemo(() => viewport === "mobile", [viewport]);

  const {
    searchValue,
    setSearchValue,
    selectedTags,
    handleTagSelection,
    selectedParts,
    handlePartSelection,
    clearAllSelections,
    isMenuOpen,
    toggleMenu,
    menuRef,
    filters,
    schedules,
  } = useScheduleFilter(props.schedules);

  const schedulesWithEventTimezone = useSchedulesWithEventTimezone(schedules);

  const renderSearchBar = React.useCallback(
    () => (
      <SearchInput
        aria-label={t("schedule.search-segments")}
        label={t("schedule.search-segments")}
        onClear={() => setSearchValue("")}
        onChange={setSearchValue}
        value={searchValue}
      />
    ),
    [searchValue, setSearchValue, t]
  );

  return (
    <BlockBackgroundColorWrapper
      ref={connect}
      id={id}
      data-testid="schedule-component"
    >
      <Wrapper>
        <BlockMaxWidthWrapper>
          <Title>
            <Element
              id="title"
              is={Text}
              text="Schedule highlights"
              element="h2"
              pattern="headingTwo"
            />
            <div className="ml-2 flex items-start">
              <FilterWrapper>
                <Popover as="div" className="filter-menu" ref={menuRef}>
                  <Filter
                    id="filter-options"
                    label="Filter"
                    totalFiltersSelected={
                      selectedTags.length + selectedParts.length
                    }
                    onClick={toggleMenu}
                    isToggled={isMenuOpen}
                  />
                  {isMenuOpen && (
                    <StyledMenuContent
                      $editing={enabled}
                      role="menu"
                      aria-labelledby="filter-options"
                    >
                      <PartsTagsDisplay
                        onClickPart={handlePartSelection}
                        onClickTag={handleTagSelection}
                        parts={filters.partOptions}
                        selectedParts={selectedParts}
                        selectedTags={selectedTags}
                        tags={filters.tagOptions}
                        onClose={toggleMenu}
                        onClear={clearAllSelections}
                      />
                    </StyledMenuContent>
                  )}
                </Popover>
              </FilterWrapper>
              {!isMobile && renderSearchBar()}
            </div>
          </Title>
          <Tabs defaultIndex={0}>
            <TimelineWrapper>
              {schedulesWithEventTimezone.length > 1 && (
                <TabList data-testid="schedule-tablist">
                  {schedulesWithEventTimezone.map((schedule, index) => {
                    return (
                      <Tab key={schedule.date}>
                        <DayWrapper>
                          <DayNumber>Day {index + 1}</DayNumber>
                          {!schedule.isNoFilters && (
                            <Notification
                              aria-label={t("schedule.total-search-results", {
                                total_search: schedule.count,
                              })}
                              role="alert"
                            >
                              {schedule.count}
                            </Notification>
                          )}
                        </DayWrapper>
                        <p>{schedule.date}</p>
                      </Tab>
                    );
                  })}
                </TabList>
              )}
              {isMobile && renderSearchBar()}
              <TimelineContent
                hideTabList={schedulesWithEventTimezone.length === 1}
              >
                <TabPanels>
                  {schedulesWithEventTimezone.map((schedule) => {
                    return (
                      <TabPanel key={schedule.date}>
                        {schedule.items.length === 0 ? (
                          <ScheduleItemWrapper>
                            <EmptyItem
                              tagsCount={selectedTags.length}
                              partsCount={selectedParts.length}
                              includesSearch={!!searchValue}
                            />
                          </ScheduleItemWrapper>
                        ) : (
                          <TimelineItem schedules={schedule.items} />
                        )}
                      </TabPanel>
                    );
                  })}
                </TabPanels>
              </TimelineContent>
            </TimelineWrapper>
          </Tabs>
        </BlockMaxWidthWrapper>
      </Wrapper>
    </BlockBackgroundColorWrapper>
  );
}

DefaultSchedule.craft = {
  custom: {
    displayName: "Default Schedule",
  },
  related: {
    leftOutlineOptions: TopLevelFloatingOptions,
    settings: ScheduleSettings,
  },
};

function TimelineItem({ schedules }: { schedules: ScheduleItem[] }) {
  const { viewport } = useViewportContext();
  const { t } = useTranslation();
  const selectedTimezone = useScheduleTimezone();

  return (
    <>
      {schedules.map((scheduleItem) => {
        const speakers = mapFirstLastNames(scheduleItem.speakers || []).map(
          (speaker) => ({
            firstName: speaker.first_name,
            lastName: speaker.last_name,
            image: speaker.image_url,
          })
        );

        const type = scheduleItem.event_part.event_part_type;
        const segment =
          scheduleItem.event_part.event_type_custom_text ??
          type.charAt(0).toUpperCase() + type.slice(1);

        const time = getFormattedScheduleTime(
          scheduleItem.time_start,
          scheduleItem.time_end,
          selectedTimezone
        );

        return (
          <ScheduleItemWrapper key={scheduleItem.id}>
            <ScheduleItemTopBar>
              <ScheduleItemTopBar>
                <ScheduleItemSegment>{segment}</ScheduleItemSegment>
                <ScheduleItemDate>{time}</ScheduleItemDate>
              </ScheduleItemTopBar>
              {(viewport === "desktop" || viewport === "tablet") && (
                <AddToCalendarWrapper>
                  <AddToCalendar
                    event={{
                      external_id: scheduleItem.external_id,
                      time_start: scheduleItem.time_start,
                      time_end: scheduleItem.time_end,
                      name: scheduleItem.name,
                    }}
                  >
                    <AddToCalendarButton
                      isInline
                      variant="outlined"
                      pattern="secondary"
                      size="small"
                      title={t("preference-panel.schedule.add-to-calendar")}
                    >
                      <Calendar className="mr-4 size-5" />
                      {t("preference-panel.schedule.add-to-calendar")}
                    </AddToCalendarButton>
                  </AddToCalendar>
                </AddToCalendarWrapper>
              )}
            </ScheduleItemTopBar>
            <ScheduleItemTitle>{scheduleItem.name}</ScheduleItemTitle>

            <ScheduleItemDescription html={scheduleItem.description} />
            {speakers.length > 0 ? (
              <AvatarGroupWrapper>
                <AvatarGroup
                  popupLabel="Additional speakers"
                  data-testid="speaker-avatars-group"
                  users={speakers}
                />
              </AvatarGroupWrapper>
            ) : null}

            {viewport === "mobile" && (
              <MobileAddToCalendarWrapper>
                <AddToCalendar
                  event={{
                    external_id: scheduleItem.external_id,
                    time_start: scheduleItem.time_start,
                    time_end: scheduleItem.time_end,
                    name: scheduleItem.name,
                  }}
                >
                  <AddToCalendarButton
                    isInline
                    variant="outlined"
                    pattern="secondary"
                    size="small"
                    title={t("preference-panel.schedule.add-to-calendar")}
                  >
                    <Calendar className="mr-4 size-5" />
                    {t("preference-panel.schedule.add-to-calendar")}
                  </AddToCalendarButton>
                </AddToCalendar>
              </MobileAddToCalendarWrapper>
            )}
          </ScheduleItemWrapper>
        );
      })}
    </>
  );
}
